import { createContext } from 'react';

import type { Tool } from '#plugin-chat/components/tools/Tool.ts';

type ToolKey = keyof typeof Tool;

/**
 * Per-agent registry of tool wire names. Keys are the {@link Tool}
 * namespace component keys (e.g. `ShowDashboard`); values are the
 * **plain wire names** the mounting agent declared on each tool
 * element (`<Tool.ShowDashboard name="showDashboard" />`) — the same
 * names the runtime records as `toolName` on tool-call parts, so flow
 * signals and thread scans compare against them directly. Format with
 * {@link toolRef} when embedding a name in prompt prose.
 *
 * Consumers read the registry via `use(ToolsContext)` and reference
 * tools by key — `tools.ShowDashboard` — without null-checks. The
 * context value is always a {@link ToolsRefs} Proxy: unknown keys log
 * a `console.error` and return a visible sentinel, so misuse is loud
 * but never fatal.
 */
export type ToolsRefs = Record<ToolKey, string>;

/**
 * Format a tool wire name as a backticked prompt-ready reference
 * (`` `name` ``). Single source of truth for the prompt-embedded ref
 * shape — applied at prose-formatting sites (e.g. the marker repair
 * prompt), never to the {@link ToolsRefs} registry values themselves.
 */
export const toolRef = (name: string): string => `\`${name}\``;

/**
 * Visible sentinel for the "we don't know this tool's wire name"
 * case — routes both unknown-key proxy lookups and the
 * `<ToolGuidance>` no-context fallback through one shape so prompts
 * read consistently.
 */
export const missingToolRef = (name: string): string => `\`<missing-tool:${name}>\``;

/**
 * Build a Proxy `get` trap that logs the misuse and returns
 * {@link missingToolRef}. Backs the default `tripwire` proxy's
 * unknown-key handling. Non-string props (e.g. Symbols) return
 * `undefined`.
 */
function missingKeyTrap(buildMessage: (prop: string) => string): NonNullable<ProxyHandler<ToolsRefs>['get']> {
  return (_target, prop) => {
    if (typeof prop !== 'string') {
      return undefined;
    }
    console.error(buildMessage(prop));
    return missingToolRef(prop);
  };
}

/**
 * Tripwire proxy used as the default context value — accessing
 * `tools.x` outside an `<AtlasAssistant>` provider logs a
 * `console.error` and returns a sentinel.
 */
const tripwire: ToolsRefs = new Proxy({} as ToolsRefs, {
  get: missingKeyTrap((prop) => `[ToolsContext] tools.${prop} accessed outside context`),
});

export const ToolsContext = createContext<ToolsRefs>(tripwire);
